/* Personalizer v2 photo upload — ONE upload group for the whole order.
   Exposes window.PersonalizerUpload.initPhotoGroup(groupEl, opts), called by
   personalizer.js against the static skeleton in
   snippets/personalizer-step-photos.liquid. XHR is used (not fetch) because
   upload progress events are needed. */
(function () {
  'use strict';

  var ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  // Cart contract requires the uploaded URL pathname to end in one of these.
  var ALLOWED_URL_RE = /\.(jpe?g|png|webp)$/i;

  function isValidUploadedUrl(url) {
    try {
      var parsed = new URL(url);
      return parsed.protocol === 'https:' && ALLOWED_URL_RE.test(parsed.pathname);
    } catch (err) {
      return false;
    }
  }

  function uploadFile(endpoint, file, onProgress) {
    return new Promise(function (resolve, reject) {
      try {
        var xhr = new XMLHttpRequest();
        var formData = new FormData();
        formData.append('file', file, file.name);
        xhr.open('POST', endpoint);
        xhr.upload.addEventListener('progress', function (e) {
          if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
        });
        xhr.addEventListener('load', function () {
          if (xhr.status < 200 || xhr.status >= 300) {
            reject(new Error('Upload failed (' + xhr.status + ')'));
            return;
          }
          var body;
          try {
            body = JSON.parse(xhr.responseText);
          } catch (err) {
            reject(new Error('Upload service returned an unexpected response'));
            return;
          }
          if (!body || !body.url || !isValidUploadedUrl(body.url)) {
            reject(new Error('Upload service returned an invalid photo URL'));
            return;
          }
          resolve(body.url);
        });
        xhr.addEventListener('error', function () { reject(new Error('Network error during upload')); });
        xhr.addEventListener('abort', function () { reject(new Error('Upload cancelled')); });
        /* Without a deadline a stalled connection leaves the tile saying
           "Uploading…" until the browser gives up minutes later, with nothing
           the customer can act on. Some networks block the upload host
           outright; better to fail in under a minute and offer Retry.
           Generous enough for a large photo on a slow phone connection. */
        xhr.timeout = 60000;
        xhr.addEventListener('timeout', function () {
          reject(new Error('Upload timed out — check your connection and retry'));
        });
        xhr.send(formData);
      } catch (err) {
        reject(err);
      }
    });
  }

  /* Klaviyo "Uploaded Photo" — fired on the FIRST successful upload per product/session.
     Klaviyo only records track calls from identified (cookied) browsers, same as
     Viewed Product, so anonymous visitors are silently skipped — harmless.
     Product context comes from the data-pz-klaviyo JSON rendered by
     personalizer-embed.liquid; tracking must never break the upload UX. */
  function trackUploadedPhoto(photoUrl) {
    try {
      var el = document.querySelector('script[data-pz-klaviyo]');
      if (!el) return;
      var data = JSON.parse(el.textContent);
      var key = 'pz:uploaded-tracked:' + (data.URL || window.location.pathname);
      if (sessionStorage.getItem(key)) return;
      // image_url can render protocol-relative — email clients need absolute https.
      if (data.ImageURL && data.ImageURL.indexOf('//') === 0) data.ImageURL = 'https:' + data.ImageURL;
      data.PhotoURL = photoUrl;
      var kl = window.klaviyo || window._learnq || (window._learnq = []);
      kl.push(['track', 'Uploaded Photo', data]);
      sessionStorage.setItem(key, '1');
    } catch (err) { /* never block the upload flow over analytics */ }
  }

  /* opts: { uploadEndpoint, getMaxPhotos(), maxFileMb, requirePhotos,
             restored: [{url,name}], onChange() }
     getMaxPhotos is a callback because the overall cap follows the live bestie
     count (maxPhotosPerBestie x besties) without rebuilding the group.
     Returns { getPhotos, isValid }. */
  function initPhotoGroup(groupEl, opts) {
    var tileTpl = document.getElementById('personalizer-photo-tile-template');
    if (!groupEl || !tileTpl) return null;

    var input = groupEl.querySelector('[data-photo-input]');
    var previews = groupEl.querySelector('[data-photo-previews]');
    var progressWrap = groupEl.querySelector('[data-photo-progress]');
    var progressFill = groupEl.querySelector('[data-photo-progress-fill]');
    var errorEl = groupEl.querySelector('[data-photo-error]');
    var noticeEl = groupEl.querySelector('[data-photo-notice]');
    var records = []; // { file, name, url, status: 'uploading'|'done'|'error', tile }
    /* File names rejected at selection time (wrong type/size). A mixed
       multi-select uploads its valid files immediately, and every later
       repaint (progress, done, failed) goes through showError — so the skip
       notice lives OUTSIDE the transient message and is re-appended on each
       repaint. It resets only on the next file-picker interaction. */
    var rejected = [];
    /* "Up to N photos for this order." is painted once per selection but the
       uploads it started repaint the slot as they finish (refreshFailedError),
       which on a fast network wiped it within a second — the customer never
       learned why 2 of their 12 photos were dropped. Keep it sticky like the
       skip notice: cleared on the next picker interaction or a tile removal. */
    var capNotice = '';

    function rejectedMsg() {
      if (!rejected.length) return '';
      return rejected.length + ' file' + (rejected.length === 1 ? '' : 's') +
        ' skipped (wrong type/size): ' + rejected.join(', ');
    }

    if (!opts.uploadEndpoint) {
      // No upload backend configured: photos are collected off-site after purchase.
      noticeEl.textContent = 'Photo upload will be collected after checkout.';
      noticeEl.hidden = false;
      groupEl.querySelector('.personalizer__file-drop').hidden = true;
      if (opts.requirePhotos) {
        // "Require photos" cannot be honored without an endpoint; validation
        // ignores it in this mode (see isValid) — warn so the merchant notices.
        console.warn('Personalizer: "requirePhotos" is on but no uploadEndpoint is configured. Photos will be collected after checkout instead.');
      }
    }

    function showError(msg) {
      // Sticky skip notice rides along with whatever transient/blocking
      // message is being painted (or alone when msg is empty).
      var text = [msg, capNotice, rejectedMsg()].filter(Boolean).join(' ');
      errorEl.textContent = text;
      errorEl.hidden = !text;
    }

    /* Failed uploads block the step (see isValid): keep the guidance visible
       until every failed tile is retried or removed. */
    function refreshFailedError() {
      var anyFailed = records.some(function (r) { return r.status === 'error'; });
      showError(anyFailed ? 'Remove or retry failed photos to continue.' : '');
    }

    /* The cap is live (max of the order floor and maxPhotosPerBestie x current
       besties), so it can SHRINK after photos were added — e.g. add 12 photos
       with 6 besties, go Back and drop to 1 bestie. Add-time enforcement alone would then silently submit
       over-budget photo lines; re-check at validation time and tell the user
       what to remove. Returns true while over the cap. */
    var capErrorShown = false;
    function refreshCapError() {
      var maxPhotos = opts.getMaxPhotos ? opts.getMaxPhotos() : 10;
      var over = records.length - maxPhotos;
      if (over > 0) {
        showError('You have ' + records.length + ' photos but the limit is now ' +
          maxPhotos + ' — remove ' + over + ' to continue.');
        capErrorShown = true;
      } else if (capErrorShown) {
        // Cap satisfied again (photos removed or bestie count raised): clear
        // our message but re-assert failed-upload guidance if any remains.
        capErrorShown = false;
        refreshFailedError();
      }
      return over > 0;
    }

    function setTileState(record, text) {
      var stateEl = record.tile.querySelector('[data-photo-state]');
      stateEl.textContent = text || '';
      stateEl.hidden = !text;
    }

    function notify() {
      if (opts.onChange) opts.onChange();
    }

    /* One bar serves the group but multi-select starts several uploads at once:
       aggregate in-flight ratios so concurrent uploads share the bar. */
    function refreshProgress() {
      var uploading = records.filter(function (r) { return r.status === 'uploading'; });
      progressWrap.hidden = uploading.length === 0;
      if (!uploading.length) return;
      var sum = 0;
      uploading.forEach(function (r) { sum += r.progress || 0; });
      progressFill.style.width = Math.round((sum / uploading.length) * 100) + '%';
    }

    function startUpload(record) {
      record.status = 'uploading';
      record.progress = 0;
      record.tile.classList.remove('personalizer__preview-tile--error');
      record.tile.querySelector('[data-photo-retry]').hidden = true;
      setTileState(record, 'Uploading…');
      refreshFailedError(); // clears the message once the last error tile is retried
      refreshProgress();
      uploadFile(opts.uploadEndpoint, record.file, function (ratio) {
        record.progress = ratio;
        setTileState(record, 'Uploading ' + Math.round(ratio * 100) + '%');
        refreshProgress();
      }).then(function (url) {
        record.url = url;
        record.status = 'done';
        setTileState(record, '');
        refreshProgress();
        refreshFailedError(); // re-assert if other tiles are still failed
        trackUploadedPhoto(url);
        notify();
      }).catch(function () {
        record.status = 'error';
        record.tile.classList.add('personalizer__preview-tile--error');
        record.tile.querySelector('[data-photo-retry]').hidden = false;
        setTileState(record, 'Failed');
        refreshProgress();
        refreshFailedError();
        notify();
      });
    }

    function addTile(record) {
      var tile = tileTpl.content.firstElementChild.cloneNode(true);
      record.tile = tile;
      tile.querySelector('[data-photo-remove]').addEventListener('click', function () {
        records.splice(records.indexOf(record), 1);
        tile.remove();
        capNotice = ''; // room again — the cap notice no longer applies
        refreshFailedError(); // clears the error slot unless other failed tiles remain
        refreshProgress(); // removing an in-flight photo must not leave a stuck bar
        notify();
      });
      tile.querySelector('[data-photo-retry]').addEventListener('click', function () {
        if (record.file) startUpload(record);
      });
      previews.appendChild(tile);
    }

    function handleFiles(fileList) {
      rejected = []; // a new selection replaces the previous skip notice
      var capMsg = '';
      var maxPhotos = opts.getMaxPhotos ? opts.getMaxPhotos() : 10;
      Array.prototype.slice.call(fileList).forEach(function (file) {
        if (records.length >= maxPhotos) {
          capMsg = 'Up to ' + maxPhotos + ' photos for this order.';
          return;
        }
        if (ALLOWED_TYPES.indexOf(file.type) === -1 ||
            file.size > opts.maxFileMb * 1024 * 1024) {
          // Mixed multi-select: valid siblings keep uploading; every invalid
          // file joins ONE sticky message painted after the loop.
          rejected.push(file.name);
          return;
        }
        var record = { file: file, name: file.name, url: null, status: 'uploading' };
        records.push(record);
        addTile(record);
        // Instant local thumbnail while the real upload runs
        var reader = new FileReader();
        reader.onload = function (e) {
          record.tile.querySelector('[data-photo-thumb]').src = e.target.result;
        };
        reader.readAsDataURL(file);
        startUpload(record);
      });
      // Painted AFTER the whole batch so mid-loop repaints (startUpload) can't
      // hide the cap message or a partial skip list.
      capNotice = capMsg;
      showError('');
      notify();
    }

    input.addEventListener('click', function () {
      // Re-opening the picker is the "next file-picker interaction" that
      // retires the sticky skip/cap notices; blocking messages repaint themselves.
      if (rejected.length || capNotice) {
        rejected = [];
        capNotice = '';
        refreshFailedError();
      }
    });

    input.addEventListener('change', function () {
      if (input.files && input.files.length) handleFiles(input.files);
      input.value = ''; // allow re-selecting the same file after a remove
    });

    // Restore previously uploaded photos (sessionStorage — URLs only, no File objects)
    (opts.restored || []).forEach(function (saved) {
      if (!saved || !saved.url) return;
      var record = { file: null, name: saved.name || '', url: saved.url, status: 'done' };
      records.push(record);
      addTile(record);
      record.tile.querySelector('[data-photo-thumb]').src = saved.url;
    });

    return {
      getPhotos: function () {
        return records
          .filter(function (r) { return r.status === 'done' && r.url; })
          .map(function (r) { return { url: r.url, name: r.name }; });
      },
      isValid: function () {
        var pending = records.some(function (r) { return r.status === 'uploading'; });
        if (pending) return false;
        // No endpoint = photos genuinely collected after checkout (the visible
        // notice promises exactly that), so the step must validate even when
        // requirePhotos is on — otherwise shipped defaults dead-end the flow.
        if (!opts.uploadEndpoint) return true;
        // A failed upload must never be silently dropped at submit: the step
        // stays invalid until each error tile is retried or removed.
        var failed = records.some(function (r) { return r.status === 'error'; });
        if (failed) return false;
        // Cap can shrink after add (bestie count reduced) — block until resolved.
        if (refreshCapError()) return false;
        var done = records.filter(function (r) { return r.status === 'done'; }).length;
        return opts.requirePhotos ? done > 0 : true;
      }
    };
  }

  window.PersonalizerUpload = {
    initPhotoGroup: initPhotoGroup,
    // Exported so personalizer.js applies the SAME rule to session-restored URLs.
    isValidUploadedUrl: isValidUploadedUrl
  };
})();
